import moment from "jalali-moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import useTableQuery from "../../hooks/useTableQuery";
import { popupSlice } from "../../services/reducers";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import tools from "../../utils/tools";
import { Sidebar } from "../../layouts/Sidebar";
import DataTable, { DataTableColumn } from "../../layouts/DataTable";
import AdvancedFilters from "./AdvancedFilters";
import Bill from "./Bill";
import FeedbackModal from "./FeedbackModal";
import Message from "../../layouts/Modal/Message";

const DEFAULT_DATE_FROM = "1403/05/01";

const Orders = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<any>({});
  const [loading, setLocalLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const defaultDateTo = useMemo(() => moment().add(1,'y').format("jYYYY/jMM/jDD"), []);
  const defaultFilters = useMemo(
    () => ({ dateFrom: DEFAULT_DATE_FROM, dateTo: defaultDateTo }),
    [defaultDateTo],
  );
  const query = useTableQuery({ defaultFilters });
  const tab = searchParams.get("tab") || "all";
  const paymentMethod = searchParams.get("paymentMethod") || "all";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tabTitles = {
    all: "همه",
    AwaitingPayment: "در انتظار پرداخت",
    Paid: "پرداخت شده",
    Assigned: "محول شده",
    InProgress: "در حال انجام",
    Canceled: "لغو شده",
    Done: "تمام شده",
  };

  const deleteOrder = async (id: number) => {
    const result = await Swal.fire({
      title: "آیا مطمئن هستید؟",
      text: "این عمل قابل بازگشت نیست",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#c89dc7",
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "انصراف",
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(
          process.env.REACT_APP_BASE_URL + "/admin/order/" + id,
          true,
        ).delete({});

        if (res.code == 200) {
          Swal.fire({
            title: "موفق",
            text: "سفارش با موفقیت حذف شد",
            icon: "success",
            confirmButtonText: "متوجه شدم",
          });
          fetchOrders();
        } else {
          Swal.fire({
            title: "ناموفق",
            text: res?.data || "خطا در حذف سفارش",
            icon: "error",
            confirmButtonText: "متوجه شدم",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "خطا",
          text: "خطا در اتصال به سرور",
          icon: "error",
          confirmButtonText: "متوجه شدم",
        });
      }

      dispatch(setLoading(false));
    }
  };

  const hideOrders = async () => {
    if (!selectedIds.length) return;

    const result = await Swal.fire({
      title: "آیا مطمئن هستید؟",
      text: `${selectedIds.length} سفارش مخفی خواهد شد. این عمل قابل بازگشت نیست`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#c89dc7",
      confirmButtonText: "بله، مخفی کن",
      cancelButtonText: "انصراف",
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(
          process.env.REACT_APP_BASE_URL + "/admin/order/hideOrders",
          true,
        ).post({ ids: selectedIds });

        if (res.code == 200) {
          Swal.fire({
            title: "موفق",
            text: `${res.data?.affected ?? selectedIds.length} سفارش با موفقیت مخفی شد`,
            icon: "success",
            confirmButtonText: "متوجه شدم",
          });
          setSelectedIds([]);
          fetchOrders();
        } else {
          Swal.fire({
            title: "ناموفق",
            text: res?.data || "خطا در مخفی کردن سفارش",
            icon: "error",
            confirmButtonText: "متوجه شدم",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "خطا",
          text: "خطا در اتصال به سرور",
          icon: "error",
          confirmButtonText: "متوجه شدم",
        });
      }

      dispatch(setLoading(false));
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: "actions",
      label: "عملیات",
      cardRole: "actions",
      render: (order) => (
        <div className="svgContainer">
          <i
            className="trash clickable iconSize"
            onClick={() => deleteOrder(order.id)}
            title="حذف"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && deleteOrder(order.id)}
          ></i>
          <i
            className="edit clickable iconSize"
            onClick={() => navigate("/order/edit/" + order.id)}
            title="ویرایش"
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && navigate("/order/edit/" + order.id)
            }
          ></i>
          <i
            className="copyIcon clickable iconSize"
            onClick={() => navigate("/order/add?copyOf=" + order.id)}
            title="کپی"
            role="button"
          ></i>
          <i
            className="usersSvg clickable iconSize"
            onClick={() => dispatch(popupSlice.middle(<Bill order={order} />))}
            title="محول کردن"
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              dispatch(popupSlice.middle(<Bill order={order} />))
            }
          ></i>
          <i
            className="messageIcon clickable iconSize"
            onClick={() =>
              dispatch(popupSlice.middle(<Message user={order.user} />))
            }
            title="پیام"
            role="button"
          ></i>
          {order.isFeedbacked && (
            <i
              className="feedbackIcon clickable"
              onClick={() =>
                dispatch(popupSlice.middle(<FeedbackModal order={order} />))
              }
              title="بازخورد"
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                dispatch(popupSlice.middle(<FeedbackModal order={order} />))
              }
            ></i>
          )}
        </div>
      ),
    },
    {
      key: "finalPrice",
      label: "مبلغ کل",
      sortable: true,
      render: (order) => tools.formatPrice(order.finalPrice),
    },
    {
      key: "worker",
      label: "استایلیست",
      searchable: true,
      render: (order) =>
        order.worker ? `${order.worker?.name} ${order.worker?.lastName}` : "-",
    },
    {
      key: "service",
      label: "خدمت",
      render: (order) => order.service?.title,
    },
    {
      key: "status",
      label: "وضعیت سفارش",
      render: (order) => order.status,
    },
    {
      key: "user",
      label: "کاربر",
      searchable: true,
      cardRole: "title",
      render: (order) => order?.user?.name + " " + order?.user?.lastName,
    },
    {
      key: "date",
      label: "تاریخ",
      sortable: true,
      render: (order) => (
        <div className="column">
          <p>{order?.date + " - " + order?.fromTime}</p>
          <p>{moment(order?.createdAt).format("jYYYY/jMM/jDD HH:mm:ss")}</p>
        </div>
      ),
    },
    {
      key: "code",
      label: "شماره سفارش",
      searchable: true,
      sortable: true,
      cardRole: "subtitle",
      render: (order) => order?.code,
    },
    {
      key: "index",
      label: "ردیف",
      render: (_order, index) => index + 1,
    },
  ];

  const fetchOrders = async () => {
    setLocalLoading(true);
    dispatch(setLoading(true));

    const params: any = { ...query.params };
    if (tab != "all") {
      params.status = tab;
    }
    if (paymentMethod != "all") {
      params.paymentMethod = paymentMethod;
    }

    try {
      const res = await restApi(endpoints.order.index, true).get(params);
      setData({
        ...res.data,
        statusCount: {
          ...res.data.statusCount,
          all: {
            title: "همه",
            count: Object.values(res.data.statusCount).reduce(
              (acc, curr) => acc + ((curr as any).count || 0),
              0,
            ),
          },
        },
      });
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire({
        title: "خطا",
        text: "خطا در دریافت اطلاعات",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      setLocalLoading(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    // Drop selections when the visible result set changes — a bulk hide should
    // only ever affect rows the user can actually see selected.
    setSelectedIds([]);
    fetchOrders();
  }, [
    query.page,
    query.perPage,
    query.sortBy,
    query.sortDir,
    JSON.stringify(query.filters),
    tab,
    paymentMethod,
  ]);

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />

        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">سفارش ها</h1>

          <div className="dashTabs">
            {Object.entries(tabTitles).map(([key, value]) => (
              <span
                key={key}
                className={`ordersTag clickable ${key == tab ? "activeTab" : ""}`}
                onClick={() => query.setParam("tab", key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    query.setParam("tab", key);
                  }
                }}
              >
                {value}
                {data?.statusCount && (
                  <span
                    className={`numberTag ${key == tab ? "activeTab" : ""}`}
                  >
                    {data?.statusCount[key]?.count || 0}
                  </span>
                )}
              </span>
            ))}
          </div>

          <AdvancedFilters
            query={query}
            defaultDateFrom={DEFAULT_DATE_FROM}
            defaultDateTo={defaultDateTo}
            paymentMethod={paymentMethod}
            paymentMethodCount={data?.paymentMethodCount}
          />

          <div className="searchContainer">
            <span className="backSpan keepRight">
              <span
                className="dashboardHeader clickable"
                onClick={() => navigate("/order/add")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate("/order/add")}
              >
                <p>ثبت سفارش جدید</p>
                <i className="plusIcon"></i>
              </span>

              <span
                className={`dashboardHeader ${selectedIds.length ? "clickable" : "dashboardHeaderDisabled"}`}
                onClick={hideOrders}
                role="button"
                tabIndex={selectedIds.length ? 0 : -1}
                aria-disabled={!selectedIds.length}
                title={
                  selectedIds.length
                    ? "مخفی کردن سفارش های انتخاب شده"
                    : "ابتدا سفارش ها را انتخاب کنید"
                }
                onKeyDown={(e) => e.key === "Enter" && hideOrders()}
              >
                <p>
                  مخفی کردن
                  {selectedIds.length > 0 && ` (${selectedIds.length})`}
                </p>
                <i className="hiddenIcon"></i>
              </span>
            </span>

            <div className="itemsPerPage">
              <label htmlFor="itemPerPage">تعداد در صفحه</label>
              <input
                id="itemPerPage"
                type="number"
                min="1"
                max="100"
                defaultValue={query.perPage}
                onChange={(input) => {
                  const value = Number(input.target.value);
                  if (value && value > 0 && value <= 100) {
                    query.setPerPage(value);
                  }
                }}
                onBlur={(e) => {
                  if (!e.target.value || Number(e.target.value) <= 0) {
                    e.target.value = query.perPage.toString();
                  }
                }}
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={data?.orders || []}
            count={data?.count || 0}
            rowKey={(order) => order.id}
            query={query}
            loading={loading}
            emptyMessage="سفارشی یافت نشد"
            selectable
            selectedKeys={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </main>
      </div>
    </>
  );
};

export default Orders;
